import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, FileText, TrendingUp, DollarSign, Users } from 'lucide-react';
import { mockPayrollRuns, mockEmployees } from '../mockData';

const Reports = () => {
  const reportTypes = [
    {
      title: 'Payroll Summary Report',
      description: 'Monthly payroll breakdown with all deductions',
      icon: DollarSign,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Employee Report',
      description: 'Complete employee database with salary details',
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Attendance Report',
      description: 'Monthly attendance and leave summary',
      icon: FileText,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Compliance Report',
      description: 'PF, ESI, TDS and statutory compliance report',
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
        <p className="text-gray-500 mt-1">Generate and download comprehensive reports</p>
      </div>

      {/* Quick Reports */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reportTypes.map((report, index) => {
          const Icon = report.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className={`${report.bgColor} ${report.color} p-3 rounded-lg`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">{report.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{report.description}</p>
                      <Button className="mt-4" size="sm">
                        <Download className="mr-2 h-4 w-4" />
                        Generate Report
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed Analytics */}
      <Card>
        <CardHeader>
          <CardTitle>Payroll Analytics</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="overview" className="w-full">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="trends">Trends</TabsTrigger>
              <TabsTrigger value="department">By Department</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500">Total Employees</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{mockEmployees.length}</p>
                      <p className="text-sm text-green-600 mt-2">+2 this month</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500">Average Salary</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">
                        ₹{Math.round(mockEmployees.reduce((sum, e) => sum + e.grossSalary, 0) / mockEmployees.length / 1000)}K
                      </p>
                      <p className="text-sm text-blue-600 mt-2">Monthly average</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500">Total Payroll</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">
                        ₹{Math.round(mockEmployees.reduce((sum, e) => sum + e.grossSalary, 0) / 1000)}K
                      </p>
                      <p className="text-sm text-purple-600 mt-2">This month</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Payroll Runs Chart Placeholder */}
                <div className="border rounded-lg p-6">
                  <h3 className="font-semibold text-lg mb-4">Payroll Trends (Last 6 Months)</h3>
                  <div className="h-64 flex items-end justify-around space-x-4">
                    {mockPayrollRuns.map((run, index) => {
                      const height = (run.netAmount / 700000) * 100;
                      return (
                        <div key={run.id} className="flex flex-col items-center flex-1">
                          <div className="w-full bg-blue-200 rounded-t-lg transition-all hover:bg-blue-300" style={{ height: `${height}%` }}>
                            <div className="text-xs text-center text-gray-700 pt-2 font-semibold">
                              ₹{(run.netAmount / 1000).toFixed(0)}K
                            </div>
                          </div>
                          <div className="text-xs text-gray-600 mt-2 text-center">
                            {run.month.split(' ')[0]}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="trends" className="mt-6">
              <div className="border rounded-lg p-8 text-center">
                <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Trend analysis coming soon</p>
              </div>
            </TabsContent>

            <TabsContent value="department" className="mt-6">
              <div className="space-y-4">
                {[...new Set(mockEmployees.map(e => e.department))].map((dept) => {
                  const deptEmployees = mockEmployees.filter(e => e.department === dept);
                  const totalSalary = deptEmployees.reduce((sum, e) => sum + e.grossSalary, 0);
                  return (
                    <Card key={dept}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{dept}</h3>
                            <p className="text-sm text-gray-500">{deptEmployees.length} employees</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">₹{(totalSalary / 1000).toFixed(0)}K</p>
                            <p className="text-sm text-gray-500">Total payroll</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;